import { useCheckbox } from '@react-aria/checkbox'
import { FocusRing, useFocusRing } from '@react-aria/focus'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useToggleState } from '@react-stately/toggle'
import { useRef } from 'react'
import { mergeProps } from 'react-aria'
import tw from 'twin.macro'
import type { TCheckboxProps } from './Checkbox.interface'
import { StyledCheckbox, StyledLabel } from './Checkbox.styles'

const Checkbox = (props: TCheckboxProps) => {
  const { id, label, isDisabled, isError, errorMessage, ariaLabel, onChange } = props
  const state = useToggleState(props)
  const ref = useRef<HTMLInputElement | null>(null)
  const { inputProps } = useCheckbox({ 'aria-label': ariaLabel ?? 'Form Checkbox', ...props }, state, ref)
  const { focusProps, isFocusVisible } = useFocusRing()

  const handleCheckboxChange = () => {
    if (onChange) {
      onChange(state.isSelected)
    }
  }

  return (
    <div>
      <StyledLabel htmlFor={id} isDisabled={isDisabled}>
        <VisuallyHidden>
          <input
            type="checkbox"
            ref={ref}
            aria-label={ariaLabel}
            onChange={handleCheckboxChange}
            {...mergeProps(focusProps, inputProps)}
          />
        </VisuallyHidden>
        <FocusRing focusRingClass="focus-ring">
          <StyledCheckbox isDisabled={isDisabled} isFocused={isFocusVisible} isError={isError}>
            <div css={[tw`hidden w-3 h-3 bg-[#fdd6b7]`, state.isSelected && tw`block`]} />
          </StyledCheckbox>
        </FocusRing>
        <span>{label}</span>
      </StyledLabel>
      {isError && errorMessage && <div tw="text-sm ml-8 py-3">{errorMessage}</div>}
    </div>
  )
}

export default Checkbox
